
import { supabase } from "@/integrations/supabase/client";
import { TextToSpeechService } from "@/services/speech/TextToSpeechService";
import { ImageMigrationService } from "@/services/photo/imageMigrationService";

/**
 * Service for publishing interviews
 */
export class PublishInterviewService {
  /**
   * Publish an interview - using a database function for reliability
   */
  static async publishInterview(interviewId: string): Promise<boolean> {
    console.log('PublishInterviewService - Publishing interview with ID:', interviewId);
    
    try {
      // Step 0: Migrate any temporary images to permanent storage BEFORE publishing
      console.log('Migrating temporary images to permanent storage...');
      await ImageMigrationService.migrateInterviewImages(interviewId);
      
      // Step 1: Call the database function to publish the interview
      // We need to cast the function name to any to bypass the TypeScript error
      const { data, error } = await supabase.rpc('publish_interview' as any, { 
        interview_id: interviewId 
      });
      
      if (error) {
        console.error('Error in publish_interview RPC:', error);
        throw new Error(`Failed to publish interview via RPC: ${error.message}`);
      }
      
      console.log('RPC result:', data);
      
      // The function returns a boolean, so we explicitly cast it to boolean
      const success = Boolean(data);
      if (!success) {
        console.error('Publish operation failed with false result');
        throw new Error('Publish operation failed');
      }
      
      // The publish_interview RPC already handles setting is_in_feeder = false
      // and updating the timestamp, so no additional update is needed
      console.log('Interview successfully published via RPC');
      
      // Step 3: Additional verification to ensure the publication status is correct
      await new Promise(resolve => setTimeout(resolve, 500)); // Short delay for DB consistency
      
      // Step 4: Verify the update was successful
      const { data: verifyData, error: verifyError } = await supabase
        .from('interviews')
        .select('publication_status, is_in_feeder, updated_at')
        .eq('id', interviewId as any)
        .maybeSingle();
      
      if (verifyError) {
        console.error('Error verifying publication status:', verifyError);
        throw new Error(`Failed to verify publication status: ${verifyError.message}`);
      }
      
      console.log('Verification result:', verifyData);
      
      if (!verifyData || (verifyData as any)?.publication_status !== 'published') {
        console.error('Publication status was not updated correctly:', (verifyData as any)?.publication_status);
        throw new Error('Failed to update publication status: Database update did not take effect');
      }
      
      console.log('Interview successfully published');
      
      // Step 5: Pre-generate TTS audio for the published interview
      try {
        console.log('Pre-generating TTS audio for interview:', interviewId);
        
        // Get the interview data to format text for speech
        const { data: interviewData, error: interviewError } = await supabase
          .from('interviews')
          .select('context, question, answer, interviewee_name, title, company')
          .eq('id', interviewId as any)
          .maybeSingle();
        
        if (interviewError || !interviewData) {
          console.warn('Could not fetch interview data for TTS pre-generation:', interviewError?.message);
        } else {
          // Format text for speech
          const formattedText = TextToSpeechService.formatTextForSpeech({
            text: '', // Fallback - will use other properties to build text
            context: (interviewData as any)?.context,
            question: (interviewData as any)?.question,
            answer: (interviewData as any)?.answer,
            intervieweeName: (interviewData as any)?.interviewee_name,
            title: (interviewData as any)?.title,
            company: (interviewData as any)?.company
          });
          
          // Pre-generate TTS audio (this will cache it for future use)
          await TextToSpeechService.generateSpeech(
            formattedText,
            'nb-NO-Wavenet-E',
            'nb-NO'
          );
          
          console.log('TTS audio pre-generated successfully for interview:', interviewId);
        }
      } catch (ttsError) {
        // Don't fail the publishing process if TTS pre-generation fails
        console.warn('TTS pre-generation failed (non-critical):', ttsError);
      }
      
      return true;
    } catch (err) {
      console.error('Critical error in publishInterview:', err);
      throw err;
    }
  }
}
